import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import Layout from './Layout';
import Slider from '@mui/material/Slider';
import { enrichSingleVerse } from '../utils/verseEnrich';

const GROWTH_AREAS = [
  'Spiritual Development',
  'Holisitic Personal Growth',
  'Relational Development',
  'Physical Development',
  'Vocational Development',
  'Missional Development',
  'Emotional Development'
];

const DEVELOPMENT_TOPICS = [
  'Christian Character and Virtues',
  'Cultrual Engagement and Worldview',
  'Dealing with Trials and Suffering',
  'Discipeship and Mentoring Others',
  'Evangelism and Sharing Your Faith',
  'Finances - Handling Money Gods Way',
  'Identity in Christ and Intrinsic Value',
  'Leadership and Servanthood',
  'Marriage and Family',
  'Mental and Emotional Health',
  'Moral and Ethical Decision-Making',
  'Overcoming Temptation and Sin',
  'Personal Faith and Relationship with God',
  'Prayer and Spiritual Disciplines',
  'Relationships and Community',
  'Sexual Purity and Enjoyment',
  'Spiritual Warfare',
  'Stewardship of the Body',
  'Time Management and Priorities',
  'Understanding the Bible',
  'Work and Stewardship'
];

const DIFFICULTY_LEVELS = [
  { value: 1, label: '1 - Foundational' },
  { value: 2, label: '2 - Basic' },
  { value: 3, label: '3 - Intermediate' },
  { value: 4, label: '4 - Advanced' },
  { value: 5, label: '5 - Deep/Complex' }
];

// Add state for new enrichment fields
const AGE_RELEVANCE_KEYS = [
  'elementary', 'middleSchool', 'highSchool', 'youngAdult', 'middleAge', 'wisdomYears'
];
const RELATIONSHIP_RELEVANCE_KEYS = [
  'youth', 'singleYoungAdult', 'singleAdult', 'dating', 'engaged', 'earlyMarriage', 'experiencedMarriage', 'marriedWithYoungKids', 'marriedWithOlderKids', 'grandparents', 'separated', 'divorced', 'widowed'
];
const DISCIPLESHIP_STAGE_KEYS = [
  'notCurrentlyBeliever', 'newBeliever', 'growingDisciple', 'discipleMaker', 'sageStage'
];
const CHRISTIAN_EXPERIENCE_KEYS = [
  'noExperienceYet', 'newToFaith', 'grewUpStillWalking', 'cameToFaithNowWalking', 'crisisLeaningIn', 'crisisLeaningOut', 'walkedAwayCurrently', 'walkedAwayNowOpen', 'walkedAwayNowReturned'
];

const EditVerse = () => {
  const { verseId } = useParams();
  const navigate = useNavigate();
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [reference, setReference] = useState('');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [question, setQuestion] = useState('');
  const [tags, setTags] = useState('');
  const [categories, setCategories] = useState('');
  const [growthArea, setGrowthArea] = useState('');
  const [devTopic, setDevTopic] = useState('');
  const [concept, setConcept] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState(1);
  const [relatedVerses, setRelatedVerses] = useState('');
  const [availableVerses, setAvailableVerses] = useState([]);
  const [contextualMeaning, setContextualMeaning] = useState('');

  // Add state for theme metadata
  const [themeMetadata, setThemeMetadata] = useState({
    emotional: 0,
    spiritual: 0,
    relational: 0,
    vocational: 0,
    physical: 0,
    missional: 0
  });

  // Add state for new enrichment fields
  const [ageRelevance, setAgeRelevance] = useState(() => Object.fromEntries(AGE_RELEVANCE_KEYS.map(k => [k, 0])));
  const [relationshipRelevance, setRelationshipRelevance] = useState(() => Object.fromEntries(RELATIONSHIP_RELEVANCE_KEYS.map(k => [k, 0])));
  const [discipleshipStageRelevance, setDiscipleshipStageRelevance] = useState(() => Object.fromEntries(DISCIPLESHIP_STAGE_KEYS.map(k => [k, 0])));
  const [christianExperienceRelevance, setChristianExperienceRelevance] = useState(() => Object.fromEntries(CHRISTIAN_EXPERIENCE_KEYS.map(k => [k, 0])));

  const [enriching, setEnriching] = useState(false);
  const [enrichMessage, setEnrichMessage] = useState('');

  useEffect(() => {
    const fetchAvailableVerses = async () => {
      try {
        const versesSnapshot = await getDocs(collection(db, 'verses'));
        const verses = versesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            reference: doc.data().reference
          }))
          .filter(verse => verse.id !== verseId);
        setAvailableVerses(verses);
      } catch (err) {
        console.error('Error fetching verses:', err);
      }
    };

    fetchAvailableVerses();
  }, [verseId]);

  // Apply Firestore verse data to form state (used on initial load and after enrich)
  const applyVerseToForm = (verseData) => {
    setVerse(verseData);
    setReference(verseData.reference || '');
    setText(verseData.text || '');
    setQuestion(verseData.reflectionQuestion || verseData.question || '');
    setUrl(verseData.bibleUrl || verseData.url || '');
    setTags(verseData.tags?.join(', ') || '');
    setCategories(verseData.categories?.join(', ') || '');
    setGrowthArea(verseData.growthArea || '');
    setDevTopic(verseData.devTopic || '');
    setConcept(verseData.concept || '');
    setDifficultyLevel(verseData.difficultyLevel || 1);
    let relVerses = verseData.relatedVerses;
    if (typeof relVerses === 'string') {
      relVerses = relVerses.split(',').map(v => v.trim()).filter(Boolean);
    }
    if (!Array.isArray(relVerses)) relVerses = [];
    setRelatedVerses(relVerses.join(', '));
    setContextualMeaning(verseData.contextualMeaning || '');

    setThemeMetadata(verseData.themeMetadata || {
      emotional: 0,
      spiritual: 0,
      relational: 0,
      vocational: 0,
      physical: 0,
      missional: 0
    });

    setAgeRelevance(verseData.ageRelevance || Object.fromEntries(AGE_RELEVANCE_KEYS.map(k => [k, 0])));
    setRelationshipRelevance(verseData.relationshipRelevance || Object.fromEntries(RELATIONSHIP_RELEVANCE_KEYS.map(k => [k, 0])));
    setDiscipleshipStageRelevance(verseData.discipleshipStageRelevance || Object.fromEntries(DISCIPLESHIP_STAGE_KEYS.map(k => [k, 0])));
    setChristianExperienceRelevance(verseData.christianExperienceRelevance || Object.fromEntries(CHRISTIAN_EXPERIENCE_KEYS.map(k => [k, 0])));
  };

  useEffect(() => {
    const fetchVerse = async () => {
      try {
        const verseDoc = await getDoc(doc(db, 'verses', verseId));
        if (verseDoc.exists()) {
          applyVerseToForm(verseDoc.data());
          console.log('Fetched verse data:', {
            growthArea: verseDoc.data().growthArea,
            devTopic: verseDoc.data().devTopic,
            allowedGrowthAreas: GROWTH_AREAS,
            allowedDevTopics: DEVELOPMENT_TOPICS
          });
        } else {
          setError('Verse not found');
        }
      } catch (err) {
        setError('Error loading verse');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchVerse();
  }, [verseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const verseRef = doc(db, 'verses', verseId);
      
      // Debug logging before save
      console.log('Saving verse with:', {
        growthArea,
        devTopic
      });

      await updateDoc(verseRef, {
        reference,
        text,
        url,
        bibleUrl: url,
        question,
        reflectionQuestion: question,
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        categories: categories.split(',').map(cat => cat.trim()).filter(cat => cat),
        growthArea,
        devTopic,
        concept,
        difficultyLevel: Number(difficultyLevel),
        relatedVerses: relatedVerses.split(',').map(ref => ref.trim()).filter(ref => ref),
        contextualMeaning,
        themeMetadata: {
          emotional: parseFloat(themeMetadata.emotional),
          spiritual: parseFloat(themeMetadata.spiritual),
          relational: parseFloat(themeMetadata.relational),
          vocational: parseFloat(themeMetadata.vocational),
          physical: parseFloat(themeMetadata.physical),
          missional: parseFloat(themeMetadata.missional)
        },
        ageRelevance,
        relationshipRelevance,
        discipleshipStageRelevance,
        christianExperienceRelevance,
        updatedAt: new Date().toISOString()
      });
      navigate('/search-verses-system');
    } catch (err) {
      setError('Error updating verse');
      console.error(err);
    }
  };

  // Add handler for theme metadata changes
  const handleThemeChange = (theme) => (event, value) => {
    setThemeMetadata(prev => ({
      ...prev,
      [theme]: value
    }));
  };

  const handleGroupSliderChange = (groupSetter, groupState) => (key) => (event, value) => {
    groupSetter({ ...groupState, [key]: value });
  };

  // Enrich this verse using verseEnrich.js (Cloud Function); then refetch and refresh form
  const handleEnrichVerse = async () => {
    setEnriching(true);
    setEnrichMessage('');
    try {
      const success = await enrichSingleVerse(verseId, (status) => {
        if (status.status === 'success') setEnrichMessage('Verse enriched and updated!');
        else if (status.status === 'error') setEnrichMessage('Error: ' + status.message);
      });
      if (success) {
        const verseDoc = await getDoc(doc(db, 'verses', verseId));
        if (verseDoc.exists()) applyVerseToForm(verseDoc.data());
      } else if (!enrichMessage) {
        setEnrichMessage('No enrichment data returned.');
      }
    } catch (err) {
      setEnrichMessage('Error enriching verse: ' + err.message);
    } finally {
      setEnriching(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white shadow-lg rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-8 pb-4 border-b">
              Edit Verse: {reference}
            </h2>
            {/* Enrich Button */}
            <div className="mb-6 flex items-center gap-4">
              <button
                type="button"
                className="px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-800 disabled:opacity-60"
                onClick={handleEnrichVerse}
                disabled={enriching}
              >
                {enriching ? 'Enriching...' : 'Enrich This Verse'}
              </button>
              {enrichMessage && <span className="text-sm text-green-700">{enrichMessage}</span>}
            </div>
          
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Reference
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (e.g., "John 3:16")
                  </span>
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700 text-lg"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Verse Text
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Bible.com URL
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (optional)
                  </span>
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                  placeholder="https://www.bible.com/bible/..."
                />
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Reflection Question
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (optional)
                  </span>
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                  placeholder="Add a reflection question..."
                />
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Tags
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (comma-separated)
                  </span>
                </label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                  placeholder="faith, love, hope"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Categories
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (comma-separated)
                  </span>
                </label>
                <input
                  type="text"
                  value={categories}
                  onChange={(e) => setCategories(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                  placeholder="Gospels, Teachings, Parables"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Personal Growth Area
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (optional)
                  </span>
                </label>
                <select
                  value={growthArea}
                  onChange={(e) => setGrowthArea(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                >
                  <option value="">Select a growth area...</option>
                  {GROWTH_AREAS.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Development Topic
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (optional)
                  </span>
                </label>
                <select
                  value={devTopic}
                  onChange={(e) => setDevTopic(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                >
                  <option value="">Select a development topic...</option>
                  {DEVELOPMENT_TOPICS.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Concept
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (optional)
                  </span>
                </label>
                <input
                  type="text"
                  value={concept}
                  onChange={(e) => setConcept(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                  placeholder="e.g., Casting Anxiety, God's Care, Trust in Difficulties"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Difficulty Level
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (1-5 scale)
                  </span>
                </label>
                <select
                  value={difficultyLevel}
                  onChange={(e) => setDifficultyLevel(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                >
                  {DIFFICULTY_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Related Verses
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (comma-separated references)
                  </span>
                </label>
                <input
                  type="text"
                  value={relatedVerses}
                  onChange={(e) => setRelatedVerses(e.target.value)}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                  placeholder="John 3:16, Romans 8:28"
                />
                <p className="text-sm text-gray-500">
                  Add references to verses that are thematically related or build upon this verse
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-lg font-semibold text-gray-800">
                  Contextual Meaning
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (How this verse applies to everyday situations)
                  </span>
                </label>
                <textarea
                  value={contextualMeaning}
                  onChange={(e) => setContextualMeaning(e.target.value)}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-2 border-gray-200 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-gray-700"
                  placeholder="Example: This verse helps when feeling overwhelmed by work or study commitments, reminding us to maintain healthy boundaries and prioritize what's truly important."
                />
              </div>

              {/* Theme Metadata Sliders */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Theme Analysis</h3>
                
                {Object.entries(themeMetadata).map(([theme, value]) => (
                  <div key={theme} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {theme} Impact
                    </label>
                    <Slider
                      value={value}
                      onChange={handleThemeChange(theme)}
                      step={0.1}
                      min={0}
                      max={1}
                      valueLabelDisplay="auto"
                      className="text-blue-600"
                    />
                  </div>
                ))}
              </div>

              {/* Age Relevance Sliders */}
              <div className="space-y-4 mt-8">
                <h3 className="text-lg font-semibold text-gray-800">Age Relevance</h3>
                {AGE_RELEVANCE_KEYS.map((key) => (
                  <div key={key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <Slider
                      value={ageRelevance[key]}
                      onChange={handleGroupSliderChange(setAgeRelevance, ageRelevance)(key)}
                      step={0.1}
                      min={0}
                      max={1}
                      valueLabelDisplay="auto"
                      className="text-green-600"
                    />
                  </div>
                ))}
              </div>

              {/* Relationship Relevance Sliders */}
              <div className="space-y-4 mt-8">
                <h3 className="text-lg font-semibold text-gray-800">Relationship Status Relevance</h3>
                {RELATIONSHIP_RELEVANCE_KEYS.map((key) => (
                  <div key={key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <Slider
                      value={relationshipRelevance[key]}
                      onChange={handleGroupSliderChange(setRelationshipRelevance, relationshipRelevance)(key)}
                      step={0.1}
                      min={0}
                      max={1}
                      valueLabelDisplay="auto"
                      className="text-yellow-600"
                    />
                  </div>
                ))}
              </div>

              {/* Discipleship Stage Relevance Sliders */}
              <div className="space-y-4 mt-8">
                <h3 className="text-lg font-semibold text-gray-800">Discipleship Stage Relevance</h3>
                {DISCIPLESHIP_STAGE_KEYS.map((key) => (
                  <div key={key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <Slider
                      value={discipleshipStageRelevance[key]}
                      onChange={handleGroupSliderChange(setDiscipleshipStageRelevance, discipleshipStageRelevance)(key)}
                      step={0.1}
                      min={0}
                      max={1}
                      valueLabelDisplay="auto"
                      className="text-purple-600"
                    />
                  </div>
                ))}
              </div>

              {/* Christian Experience Relevance Sliders */}
              <div className="space-y-4 mt-8">
                <h3 className="text-lg font-semibold text-gray-800">Christian Experience Relevance</h3>
                {CHRISTIAN_EXPERIENCE_KEYS.map((key) => (
                  <div key={key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <Slider
                      value={christianExperienceRelevance[key]}
                      onChange={handleGroupSliderChange(setChristianExperienceRelevance, christianExperienceRelevance)(key)}
                      step={0.1}
                      min={0}
                      max={1}
                      valueLabelDisplay="auto"
                      className="text-pink-600"
                    />
                  </div>
                ))}
              </div>

              <div className="pt-6 border-t mt-8 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/search-verses-system')}
                  className="px-6 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default EditVerse;
